import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEchoStore } from '@/context/EchoContext';

// Reusing existing modular components for DRY consistency
import { SidebarHeader } from './components/header/SidebarHeader';
import { SidebarNav } from './components/nav/SidebarNav';
import { SidebarFooter } from './components/footer/SidebarFooter';
import { SidebarUpgrade } from './components/upgrade/SidebarUpgrade';

/**
 * Props for the MobileSideBar component.
 */
interface MobileSideBarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  onSupport: () => void;
}

/**
 * MobileSideBar Component
 *
 * Renders a slide-over drawer covering 90% of the screen width.
 * Uses Framer Motion for smooth entrance/exit animations.
 * Reuses desktop sidebar sub-components to maintain design consistency.
 */
export const MobileSideBar: React.FC<MobileSideBarProps> = ({
  isOpen,
  onClose,
  onLogout,
  onSupport,
}) => {
  const { myEchoes } = useEchoStore();
  const echoCount = myEchoes.length;
  // Calculate total fans across all created echoes
  const totalFans = myEchoes.reduce((sum, echo) => sum + (echo.total_fans || 0), 0);

  // Animation variants for the drawer
  const drawerVariants = {
    closed: {
      x: '-100%',
      opacity: 0,
      transition: { type: 'tween', duration: 0.25, ease: 'easeIn' },
    },
    open: {
      x: '0%',
      opacity: 1,
      transition: { type: 'tween', duration: 0.3, ease: 'easeOut' },
    },
  };

  // Animation variants for the backdrop
  const backdropVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial='closed'
            animate='open'
            exit='closed'
            variants={backdropVariants}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className='fixed inset-0 z-40 bg-black/50 xl:hidden'
          />

          {/* Drawer Container */}
          <motion.aside
            initial='closed'
            animate='open'
            exit='closed'
            variants={drawerVariants}
            className={clsx(
              'fixed inset-y-0 left-0 z-50 flex flex-col',
              'h-dvh pb-[env(safe-area-inset-bottom)]',
              'w-[90%] max-w-[272px]',
              'bg-white shadow-2xl',
              'overflow-hidden rounded-r-24',
              'xl:hidden',
              'will-change-transform'
            )}
          >
            {/* Close Button (Top Right of Drawer) */}
            <button
              onClick={onClose}
              className='absolute right-4 top-4 z-50 flex size-8 items-center justify-center rounded-full bg-red-100 text-red-500 transition-colors hover:bg-red-200 active:scale-95'
              aria-label='Close menu'
            >
              <XMarkIcon className='size-5' />
            </button>

            {/* MAIN LAYOUT — all sections use dvh-based sizing to fit any screen */}
            <div className='flex h-full flex-col'>
              {/* 1. HEADER */}
              <SidebarHeader isCollapsed={false} echoCount={echoCount} fanCount={totalFans} />

              {/* 2. SPACER (Top) */}
              <div className='min-h-[clamp(0px,0.5dvh,10px)]' />

              {/* 3. NAVIGATION SECTION */}
              <SidebarNav isCollapsed={false} onItemClick={onClose} />

              {/* 4. SPACER (flexible — absorbs remaining space) */}
              <div className='min-h-0 flex-1' />

              {/* 5. UPGRADE SECTION */}
              <SidebarUpgrade isCollapsed={false} />

              {/* 6. FOOTER */}
              <SidebarFooter isCollapsed={false} onLogout={onLogout} onSupport={onSupport} />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
